import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Save, UserCircle2 } from "lucide-react";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { useAuth } from "../lib/auth";
import { api, errorMessage } from "../lib/api";

export default function Profile() {
  const { user, refresh, loading } = useAuth();
  const navigate = useNavigate();

  // Only decide about auth AFTER the initial /auth/me check finishes. On a hard refresh
  // `user` is briefly null while loading; redirecting then would bounce a logged-in user
  // to /login. Wait for `loading` to settle before sending anyone away.
  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#0A0E1A]">
        <Navbar />
        <div className="max-w-2xl mx-auto px-5 sm:px-8 py-24 flex justify-center" data-testid="profile-loading">
          <span className="spinner" />
        </div>
        <Footer />
      </div>
    );
  }

  return <ProfileForm user={user} refresh={refresh} />;
}

function ProfileForm({ user, refresh }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: user.name || "",
    personal_email: user.personal_email || "",
    phone: user.phone || "",
    linkedin_url: user.linkedin_url || "",
    college_name: user.college_name || "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/profile", form);
      await refresh();
      toast.success("Profile updated");
    } catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };

  const field = (label, key, type = "text", placeholder = "") => (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      <input type={type} value={form[key]} onChange={set(key)} placeholder={placeholder}
        className="input-field w-full" data-testid={`profile-${key}`} />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0E1A]">
      <Navbar />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-16" data-testid="profile-page">
        <div className="flex items-center gap-3 mb-2">
          <UserCircle2 className="w-7 h-7 text-brand" strokeWidth={1.5} />
          <h1 className="font-display text-3xl text-white">Your profile</h1>
        </div>
        <p className="text-sm text-gray-500 mb-8">Update your contact details. Your college email is your login identity and can't be changed here.</p>

        <div className="card-surface space-y-5">
          <div>
            <label className="label-eyebrow block mb-2 flex items-center gap-1.5"><Lock className="w-3 h-3" /> College email (login)</label>
            <input value={user.college_email || ""} disabled
              className="input-field w-full opacity-60 cursor-not-allowed" data-testid="profile-college-email" />
          </div>
          {field("Full name", "name")}
          {field("Personal email", "personal_email", "email", "you@gmail.com")}
          {field("Contact / WhatsApp number", "phone", "tel", "+91…")}
          {field("LinkedIn", "linkedin_url", "text", "linkedin.com/in/…")}
          {field("College name", "college_name")}

          <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2 w-full justify-center" data-testid="profile-save">
            {saving ? <span className="spinner" /> : <><Save className="w-4 h-4" /> Save changes</>}
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
}
